/*
 * Copyright (C) 2025-2026 The Klangmotor Authors (see AUTHORS.MD)
 * SPDX-License-Identifier: AGPL-3.0-or-later
 */

package io.peekandpoke.klang.audio_engine

import io.peekandpoke.klang.audio_be.AudioAnalyzer
import io.peekandpoke.klang.audio_be.AudioBackend
import io.peekandpoke.klang.audio_be.AudioBackendContext
import io.peekandpoke.klang.audio_bridge.KlangPlaybackSignal
import io.peekandpoke.klang.audio_bridge.KlangTime
import io.peekandpoke.klang.audio_bridge.infra.KlangCommLink
import io.peekandpoke.klang.audio_fe.samples.Samples
import io.peekandpoke.klang.common.infra.KlangAtomicInt
import io.peekandpoke.klang.common.infra.KlangLock
import io.peekandpoke.klang.common.infra.withLock
import io.peekandpoke.ultra.streams.Stream
import io.peekandpoke.ultra.streams.StreamSource
import io.peekandpoke.ultra.streams.Unsubscribe
import io.peekandpoke.ultra.streams.ops.distinct
import io.peekandpoke.ultra.streams.ops.filterIsInstanceOf
import io.peekandpoke.ultra.streams.ops.map
import io.peekandpoke.ultra.streams.ops.ticker
import kotlinx.coroutines.CancellationException
import kotlinx.coroutines.CompletableDeferred
import kotlinx.coroutines.CoroutineDispatcher
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.Dispatchers
import kotlinx.coroutines.Job
import kotlinx.coroutines.SupervisorJob
import kotlinx.coroutines.delay
import kotlinx.coroutines.isActive
import kotlinx.coroutines.launch
import kotlinx.coroutines.withContext

class KlangPlayer(
    /** The player config */
    val options: Options,
    /** Player Backend factory */
    private val backendFactory: suspend (config: AudioBackend.Config) -> AudioBackend,
    /** The coroutines scope on which the player runs */
    private val scope: CoroutineScope = CoroutineScope(SupervisorJob() + Dispatchers.Default),
    /** The dispatcher used for the event fetcher */
    private val fetcherDispatcher: CoroutineDispatcher,
    /** The dispatcher used for the audio backend */
    private val backendDispatcher: CoroutineDispatcher,
    /** The dispatcher used for frontend callbacks */
    private val callbackDispatcher: CoroutineDispatcher,
) {
    companion object {
        private val nextPlaybackId = KlangAtomicInt(0)
        private fun generatePlaybackId(): String = "playback-${nextPlaybackId.getAndIncrement()}"
    }

    data class Options(
        /** The samples */
        val samples: Samples,
        /** The sample rate to use for audio playback */
        val sampleRate: Int = 48_000,
        /**
         * The audio rendering block size.
         *
         * Defaults to the canonical [AudioBackendContext.RENDER_QUANTUM_FRAMES] — the browser
         * worklet is forced to this value by Web Audio, and block size is a tone parameter, so
         * every other host must match it or a render won't sound like live playback.
         */
        val blockSize: Int = AudioBackendContext.RENDER_QUANTUM_FRAMES,
        /**
         * The master volume to start with, a linear gain from 0.0 to 1.0 (KLANG-00330): the backend builds its output
         * at this level, so a remembered low volume never starts at full and glides down.
         */
        val outputVolume: Double = 1.0,
    )

    // Shared communication link and backend
    val commLink = KlangCommLink(capacity = 8192)
    private var backendJob: Job? = null

    // Hold reference to active backend for visualizer access
    private var _activeBackend: AudioBackend? = null

    // The master volume (KLANG-00330), kept so a backend that starts later gets it too
    private var outputVolume: Double = options.outputVolume

    // Thread-safe state management
    private val lock = KlangLock()

    // The active playbacks: the one store, changed only under the lock
    private val _playbacks = StreamSource<List<KlangPlayback>>(emptyList())

    // Signal stream for system-wide signals (diagnostics, etc.)
    private val _signals = StreamSource<KlangPlaybackSignal>(KlangPlaybackSignal.Idle)
    val signals = _signals.readonly

    /**
     * The diagnostics (KLANG-00385): [KlangDiagnostics.stream], the latest from the backend, and the metrics any owner
     * registers, with [KlangDiagnostics.history], their last two minutes, kept from the start to the shutdown. The
     * player owns the Motor's three; a playback its pattern work. Subscribe and draw; the player keeps them.
     */
    val diagnostics = KlangDiagnostics(
        stream = signals.filterIsInstanceOf(KlangPlaybackSignal.Diagnostics::class).map { it?.diagnostics },
        ticks = ticker(intervalMs = KlangDiagnostics.TICK_MS),
        capacity = KlangDiagnostics.TWO_MINUTES,
        nowMs = KlangTime.create()::internalMsNow,
    )

    // The Motor's own metrics, the player their owner: posted from every diagnostics the backend sends, closed at
    // the shutdown. Cylinders and voices are both counts, in one graph (the maintainer, 2026-10-29: "combine active
    // voices and active cylinders into one graph")
    private val motorHeat = diagnostics.metric(
        id = "motor-heat", name = "Motor heat", category = "CPU", unit = "%", min = 0.0, max = 100.0,
    )
    private val motorCylinders = diagnostics.metric(
        id = "motor-cylinders", name = "Active cylinders", category = "Cylinders and voices", unit = "", min = 0.0, max = 32.0,
    )
    private val motorVoices = diagnostics.metric(
        id = "motor-voices", name = "Active voices", category = "Cylinders and voices", unit = "", min = 0.0, max = 100.0,
    )

    private var motorFeed: Unsubscribe? = diagnostics.stream.subscribeToStream { d ->
        if (d != null) {
            motorHeat.post((1.0 - d.renderHeadroom) * 100.0)
            motorCylinders.post(d.cylinders.count { it.active }.toDouble())
            motorVoices.post(d.activeVoiceCount.toDouble())
        }
    }

    private var feedbackDispatcherJob: Job? = null
    private var hasLoggedDiagnostics = false

    // Centralized sample preloader (shared across all playbacks)
    val samplePreloader = SamplePreloader(
        samples = options.samples,
        sendControl = ::sendControl,
        scope = scope,
        dispatcher = fetcherDispatcher,
    )

    // Completes once the backend has emitted Feedback.BackendReady (post-warmup).
    val backendReady = CompletableDeferred<Unit>()

    // The backend's timeline on the frontend (KLANG-00399): moved on by the SYSTEM Diagnostics, read by every playback.
    // (Inline-Ignitor and engine registries are playbackId-bound — they live on each playback now (InlineDslRegistrar), not the scheduler.)
    private val clock = PlaybackClock()

    // Context bundle for playback implementations (reduces constructor parameter lists)
    val playbackContext = KlangPlaybackContext(
        playerOptions = options,
        samplePreloader = samplePreloader,
        sendControl = ::sendControl,
        scope = scope,
        fetcherDispatcher = fetcherDispatcher,
        callbackDispatcher = callbackDispatcher,
        backendReady = backendReady,
        clock = clock,
        diagnostics = diagnostics,
    )

    /** The active playbacks now; [playbacks] follows them. */
    val activePlaybacks: List<KlangPlayback>
        get() = lock.withLock { _playbacks() }

    /**
     * The active playbacks, anew whenever one starts or stops. Its handlers (and [playing]'s) run at once, inside the
     * player's lock, on the thread that starts or stops: they must not start or stop a playback, nor block.
     */
    val playbacks: Stream<List<KlangPlayback>> = _playbacks.readonly

    /**
     * Whether something plays: a cyclic playback is active (a song, an example, a jingle, a sample's preview). The
     * realtime voices are not counted: always on while their page lives, they would never let it go (the app keeps the
     * screen awake on this).
     */
    val playing: Stream<Boolean> = playbacks.map { all -> all.any { it is KlangCyclicPlayback } }.distinct()

    init {
        diagnostics.start()

        // Start the audio backend when the player is created
        backendJob = scope.launch {
            val backend = backendFactory(
                AudioBackend.Config(
                    commLink = commLink.backend,
                    sampleRate = options.sampleRate,
                    blockSize = options.blockSize,
                ),
            )

            // Store backend reference for visualizer access
            _activeBackend = backend
            backend.setOutputVolume(outputVolume)

            // backendDispatcher is a single-thread pinned dispatcher (see index_jvm.kt
            // / index_js.kt). No `limitedParallelism(1)` needed — and it would only
            // add per-resume re-enqueue overhead on top of an already-serial dispatcher.
            launch(backendDispatcher) {
                backend.run(this)
            }
        }

        // Start feedback dispatcher - the sole consumer of feedback messages
        // Use scope's default dispatcher (Dispatchers.Default) to avoid blocking fetcherDispatcher
        feedbackDispatcherJob = scope.launch {
            while (isActive) {
                val feedback = commLink.frontend.feedback.receive()

                if (feedback == null) {
                    delay(16) // Avoid busy-wait when no messages available
                    continue
                }

                // Handle feedback by type
                when (feedback) {
                    // One-shot backend warmup handshake
                    is KlangCommLink.Feedback.BackendReady -> {
                        backendReady.complete(Unit)
                    }

                    // Sample received acknowledgements go to preloader (shared across all playbacks)
                    is KlangCommLink.Feedback.SampleReceived -> {
                        samplePreloader.handleSampleReceived(feedback.req)
                    }

                    // System messages go to player signals
                    is KlangCommLink.Feedback.Diagnostics -> {
                        // The backend's position: the diagnostics carry its timeline.
                        clock.onDiagnostics(feedback)

                        if (!hasLoggedDiagnostics) {
                            hasLoggedDiagnostics = true
                            println(
                                "[KlangPlayer] Diagnostics: " +
                                        "sampleRate=${feedback.sampleRate} (FE=${options.sampleRate}), " +
                                        "baseLatency=${feedback.baseLatencyMs}ms, " +
                                        "deviceLatency=${feedback.outputDeviceLatencyMs}ms, " +
                                        "measuredLatency=${feedback.outputLatencyMs}ms"
                            )
                        }

                        if (feedback.playbackId == KlangCommLink.SYSTEM_PLAYBACK_ID) {
                            withContext(callbackDispatcher) {
                                _signals(KlangPlaybackSignal.Diagnostics(feedback))
                            }
                        }
                    }

                    // Playback-specific messages go to the matching playback
                    else -> {
                        val playback = lock.withLock {
                            _playbacks().find { it.playbackId == feedback.playbackId }
                        }
                        playback?.handleFeedback(feedback)
                    }
                }
            }
        }

        println("[KlangPlayer] Init complete, backendJob=$backendJob, feedbackDispatcherJob=$feedbackDispatcherJob")
    }

    /**
     * Get the audio analyzer for visualization data.
     */
    fun getAnalyzer(): AudioAnalyzer? = _activeBackend?.analyzer

    /**
     * The master volume, a linear gain from 0.0 to 1.0 (KLANG-00330): see [AudioBackend.setOutputVolume]. Before the
     * backend exists the value is kept and handed over when it starts.
     */
    fun setOutputVolume(gain: Double) {
        outputVolume = gain
        _activeBackend?.setOutputVolume(gain)
    }

    /**
     * Register a playback.
     * Called by playback implementations (like StrudelPlayback) during initialization.
     */
    fun registerPlayback(playback: KlangPlayback) {
        lock.withLock {
            _playbacks(_playbacks() + playback)
        }
    }

    /**
     * Unregister a playback.
     * Called when a playback is stopped.
     */
    fun unregisterPlayback(playback: KlangPlayback) {
        lock.withLock {
            _playbacks(_playbacks() - playback)
        }
    }

    /**
     * Gets or creates the realtime voice playback for [name] — an always-on playback that plays
     * voices "now" (MIDI keyboard & friends; see KLANG-00228 (midi keyboard playground)).
     *
     * The id is mangled to `"custom-$name"` so caller-supplied names can never collide with the
     * generated `"playback-N"` ids. Idempotent per name: a page remount reuses the existing
     * instance instead of leaking engines.
     */
    fun createRealtimePlayback(name: String): KlangRealtimeVoicePlayback {
        val playbackId = "custom-$name"

        lock.withLock {
            _playbacks().filterIsInstance<KlangRealtimeVoicePlayback>()
                .firstOrNull { it.playbackId == playbackId }
                ?.let { return it }

            return KlangRealtimeVoicePlayback(
                player = this,
                playbackId = playbackId,
                registrar = playbackContext.registrarFor(playbackId),
            ).also { _playbacks(_playbacks() + it) }
        }
    }

    /**
     * Generate a unique playback ID.
     */
    fun generatePlaybackId(): String = Companion.generatePlaybackId()

    /**
     * Send a control message to the backend.
     * This is the only way playbacks should communicate with the backend.
     */
    fun sendControl(cmd: KlangCommLink.Cmd) {
        commLink.frontend.control.send(cmd)

        // Hand it over immediately rather than leaving it for the backend's next poll. On JS that
        // poll is a 10 ms `setTimeout`, which is invisible for scheduled voices (they carry their
        // own start time) but is pure added latency for a realtime note-on, where the command IS
        // the note. No-op until the backend exists, and on backends that drain every block anyway.
        try {
            _activeBackend?.pump()
        } catch (t: Throwable) {
            if (t is CancellationException) {
                throw t
            }

            // sendControl MUST NOT throw; cancellation alone is RETHROWN above (said precisely
            // because on Kotlin/JS CancellationException extends IllegalStateException, so the
            // `catch (e: Exception)` call sites in KlangPatternScheduler would swallow it - the
            // guarantee is about what sendControl does, not about what survives the stack).
            // Before pump() existed it could not throw at all: the ring's send()
            // reports overflow by returning false. pump() runs real delivery work on the caller's
            // stack (postMessage can raise, and a Sample.Complete allocates a full second copy of
            // the PCM), and callers are not written for that. The sharp one is
            // KlangPatternScheduler.requestNextCyclesAndAdvanceCursor: it sends inside a try whose
            // catch swallows, and advances its cursor only AFTER the send — so a throw there means
            // the same batch is re-queried forever, freezing the main thread and killing playback.
            // The command that was mid-flight is lost either way (drainControl already popped it);
            // losing one is vastly better than a hung tab, and the drain continues on the next call.
            println("[KlangPlayer] backend pump failed, command dropped: $t")
        }
    }

    /**
     * Stop all playbacks and shut down the backend.
     */
    fun shutdown() {
        lock.withLock {
            // Stop each playback of the snapshot: each stop unregisters itself (the lock is re-entrant)
            _playbacks().forEach { it.stop() }
            _playbacks(emptyList())

            // Shutdown the backend and clear reference
            backendJob?.cancel()
            backendJob = null
            _activeBackend = null

            // Shutdown the feedback dispatcher
            feedbackDispatcherJob?.cancel()
            feedbackDispatcherJob = null

            // Clear preloader cache
            samplePreloader.clear()

            // Clear signal listeners, the diagnostics first: the Motor's metrics are the player's, closed with it
            motorFeed?.invoke()
            motorFeed = null
            motorHeat.close()
            motorCylinders.close()
            motorVoices.close()
            diagnostics.stop()
            _signals.removeAllSubscriptions()
        }
    }
}
